import { useSyncExternalStore } from 'react';
import { createShoppingBasket, updateShoppingBasket } from '../../common/fetch.js';
import { AuthenticatedUserRole } from '../../generated/api/authenticatedUserRole.js';
import { ShoppingCart } from '../../generated/api/shoppingCart.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { SHOPPING_BASKET_LOCAL_STORAGE_KEY } from '../../selfservice/common/localStorageUtils.js';
import { submitGiosg } from '../utils/giosgUtils.js';
import { isInBrowser } from '../utils/ssrUtils.js';
import type { ShoppingBasketType } from '../types/shoppingBasket';

const createEmptyBasket = (): ShoppingBasketType => {
  return {
    id: undefined, // ID will be generated when posting to ui-api
    items: [],
    vouchers: [],
  };
};

export const parseBasketJson = (rawBasketData?: string): ShoppingBasketType => {
  try {
    if (rawBasketData) {
      const basket = JSON.parse(rawBasketData);
      return basket as ShoppingBasketType;
    }
  } catch (error) {
    // eslint-disable-next-line
    console.error('Failed to parse basket JSON:', error);
  }
  return createEmptyBasket();
};

export const getShoppingBasketFromLocalStorage = (): string => {
  return (isInBrowser() && localStorage.getItem(SHOPPING_BASKET_LOCAL_STORAGE_KEY)) || '';
};

export const getShoppingBasketIdFromLocalStorage = (): string | undefined => {
  const rawBasket = (isInBrowser() && localStorage.getItem(SHOPPING_BASKET_LOCAL_STORAGE_KEY)) || '';
  return parseBasketJson(rawBasket).id;
};

export const shouldSendBasketToApi = (userRole: AuthenticatedUserRole): boolean => {
  return ![AuthenticatedUserRole.EMPLOYEE, AuthenticatedUserRole.PUNCHOUT_USER].includes(userRole);
};

const createShoppingBasketApiRequest = (basket: ShoppingBasketType): ShoppingCart => {
  return {
    type: ShoppingCart.TypeEnum.ACCOUNT,
    items:
      basket.items?.map(item => ({
        offerCode: item.offer.guid,
        commercialProductCode: item.offer.commercialProductGuid,
        quantity: item.quantity,
      })) || [],
  };
};

const createBasketId = async (parsedBasket: ShoppingBasketType): Promise<string | undefined> => {
  const shoppingCartRequest = createShoppingBasketApiRequest(parsedBasket);
  const response = await createShoppingBasket(shoppingCartRequest);
  return response?.shoppingCartId;
};

const persistBasketToLocalStorage = (parsedBasket: ShoppingBasketType): void => {
  const serialized = JSON.stringify(parsedBasket);
  if (isInBrowser()) {
    localStorage.setItem(SHOPPING_BASKET_LOCAL_STORAGE_KEY, serialized);
  }
  window.dispatchEvent(new StorageEvent('storage', { key: SHOPPING_BASKET_LOCAL_STORAGE_KEY, newValue: serialized }));
};

/**
 * Ensures the shopping basket in localStorage has a backend-assigned `id`.
 * Handles the case where items were added on a public page (without auth API calls)
 * but checkout needs the backend-assigned shoppingCartId.
 * Returns `true` if the basket already has an id, is empty, or the id was successfully created.
 */
export const ensureShoppingBasketId = async (): Promise<boolean> => {
  const parsedBasket = parseBasketJson(getShoppingBasketFromLocalStorage());
  if (parsedBasket.id ?? !parsedBasket.items?.length) {
    return true;
  }

  try {
    const id = await createBasketId(parsedBasket);
    if (id) {
      parsedBasket.id = id;
      persistBasketToLocalStorage(parsedBasket);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * NOTE, after 1st phase, best to remove exporting this and just use useShoppingBasket() hook,
 * then the auth stuff won't be needed to pass forward. Keeping this exported since the calls comes
 * places (epics, utils), where hooks cannot be used.
 */
export const handleShoppingBasketUpdate = async (basket: string, sendShoppingBasketToApi: boolean) => {
  const prevBasket = getShoppingBasketFromLocalStorage();
  // Only set the basket when there's actual changes, otherwise syncing cart via LocalStorage between multiple tabs
  // will cause infinite loop.
  if (prevBasket !== basket) {
    const parsedBasket = parseBasketJson(basket);
    const timestamp = new Date().getTime();
    if (!parsedBasket.created) {
      parsedBasket.created = timestamp;
    }
    parsedBasket.modified = timestamp;

    if (sendShoppingBasketToApi) {
      if (parsedBasket.id) {
        const shoppingCartRequest = createShoppingBasketApiRequest(parsedBasket);
        const response = await updateShoppingBasket(parsedBasket.id, shoppingCartRequest);
        if (response) {
          parsedBasket.id = response.shoppingCartId;
        }
      } else {
        const id = await createBasketId(parsedBasket);
        if (id) {
          parsedBasket.id = id;
        }
      }
    }
    submitGiosg(parsedBasket);
    persistBasketToLocalStorage(parsedBasket);
  }
};

export function useShoppingBasket() {
  const { isAuthenticated, authenticatedUser } = useAuth();
  const sendShoppingBasketToApi =
    isAuthenticated && authenticatedUser?.userRole && shouldSendBasketToApi(authenticatedUser.userRole);
  const setShoppingBasket = (newValue: string) => {
    isInBrowser() && handleShoppingBasketUpdate(newValue, Boolean(sendShoppingBasketToApi));
  };

  if (!getShoppingBasketFromLocalStorage()) {
    isInBrowser() && localStorage.setItem(SHOPPING_BASKET_LOCAL_STORAGE_KEY, JSON.stringify(createEmptyBasket()));
  }
  const getSnapshot = () => getShoppingBasketFromLocalStorage();

  const subscribe = (listener: () => void) => {
    window.addEventListener('storage', listener);
    return () => void window.removeEventListener('storage', listener);
  };

  const shoppingBasket = useSyncExternalStore(subscribe, getSnapshot, () => '');

  return { shoppingBasket, setShoppingBasket };
}
